import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../context/AuthContext';
import { RefreshCw, Download, Info } from 'lucide-react';

// License & role-hygiene audit panel for the SAP Role Maintenance page.
// Data source: GET /api/sap/roles/license-audit (backend sapLicenseAudit.js) - cached catalog.
const FIND = {
  'lic-under':    { label: 'Limited scope too broad', tone: 'bg-rose-50 text-rose-700 border-rose-200' },
  'lic-down':     { label: 'Downgrade candidate',    tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  'lic-none':     { label: 'No license',    tone: 'bg-slate-100 text-slate-600 border-slate-200' },
  'dormant':      { label: 'Dormant',          tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  'role-expired': { label: 'Expired role',     tone: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export default function LicensePanel({ connId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await api.get('/api/sap/roles/license-audit', { params: { connectionId: connId } });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Failed to load the license audit.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [connId]);

  const rows = useMemo(() => {
    if (data == null) return [];
    const term = q.trim().toUpperCase();
    return data.rows.filter(r =>
      (filter === '' || r.findings.includes(filter)) &&
      (term === '' || r.username.includes(term) ||
       (r.department || '').toUpperCase().includes(term) ||
       (r.company || '').toUpperCase().includes(term)));
  }, [data, filter, q]);

  const exportCsv = () => {
    if (rows.length === 0) return;
    const head = ['User', 'Full name', 'Company', 'Department', 'Type', 'License', 'License name',
      'Roles', 'Expired roles', 'Modules', 'Execution tcodes', 'Granted by', 'Last logon', 'Idle (days)', 'Findings'];
    const esc = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const body = rows.map(r => [r.username, r.fullName, r.company, r.department, r.userType,
      r.license, r.licenseText, r.roles, r.expiredRoles, r.modules.join(' '),
      r.execTcodes.join(' '), (r.execGrantedBy || []).join('; '), r.lastLogon || '', r.idleDays == null ? '' : r.idleDays,
      r.findings.map(f => (FIND[f] ? FIND[f].label : f)).join(' ')]);
    const blob = new Blob(['﻿' + [head.map(esc).join(','), ...body.map(b => b.map(esc).join(','))].join('\r\n')],
      { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'license_role_audit_' + connId + '.csv';
    a.click();
  };

  const s = data ? data.summary : null;
  const Kpi = ({ n, label, tone }) => (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className={'text-2xl font-semibold ' + (tone || 'text-slate-800')}>{n}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={load} disabled={loading}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        <button onClick={exportCsv} disabled={rows.length === 0}
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
          <Download size={14} /> Export CSV
        </button>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search user / dept / company..."
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm flex-1 min-w-[200px]" />
        <span className="text-sm text-slate-500">{rows.length} of {data ? data.rows.length : 0} users</span>
      </div>

      {err && <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-4 py-3">{err}</div>}

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <Kpi n={s.usersInScope} label="Active users analysed" />
          <Kpi n={s.withFinding} label="With findings" tone="text-rose-600" />
          <Kpi n={s.licUnder} label="Limited Pro spanning 3+ modules" tone="text-rose-600" />
          <Kpi n={s.licDown} label="Professional, narrow scope" tone="text-amber-600" />
          <Kpi n={s.dormant} label="Dormant" />
          <Kpi n={s.roleExpired} label="Expired role assignments" />
        </div>
      )}

      <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs px-4 py-3 flex gap-2">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          License classes are taken as-is from USR06 (SAP User Maintenance snapshot); the commercial names must be confirmed against the contract / USMM before being used for licensing decisions. <b>Dormant</b> status and user type come from <b>USR02</b> (TRDAT / USTYP) pulled during Load Roles; the dashboard logon log is only a fallback when TRDAT is unavailable. Execution access is detected from transaction codes inside each role; authorization field values (activity 01/02 vs 03) are not evaluated, so a display-only role that still carries an execution tcode will show up here - check the "Granted by" column in the CSV before acting.
        </span>
      </div>

      {data && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setFilter('')}
            className={'px-3 py-1 rounded-full border text-xs ' + (filter === '' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200')}>All</button>
          {Object.keys(FIND).map(k => (
            <button key={k} onClick={() => setFilter(filter === k ? '' : k)}
              className={'px-3 py-1 rounded-full border text-xs ' + (filter === k ? 'bg-slate-800 text-white border-slate-800' : FIND[k].tone)}>
              {FIND[k].label}
            </button>
          ))}
          {data.licenseMix.map(m => (
            <span key={m.code} className="px-3 py-1 rounded-full border border-slate-200 bg-white text-xs text-slate-500">
              {m.code}: {m.n}
            </span>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Company</th>
              <th className="text-left px-3 py-2">Dept</th>
              <th className="text-left px-3 py-2">License</th>
              <th className="text-right px-3 py-2">Roles</th>
              <th className="text-left px-3 py-2">Modules</th>
              <th className="text-right px-3 py-2">Idle</th>
              <th className="text-left px-3 py-2">Findings</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.username} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium text-slate-800">{r.username}
                  <div className="text-xs text-slate-400">{r.fullName}</div></td>
                <td className="px-3 py-2 text-slate-600">{r.company}</td>
                <td className="px-3 py-2 text-slate-600">{r.department}</td>
                <td className="px-3 py-2 text-slate-600">{r.license || '-'}
                  <div className="text-xs text-slate-400">{r.licenseText}</div></td>
                <td className="px-3 py-2 text-right tabular-nums">{r.roles}
                  {r.expiredRoles > 0 && <span className="text-xs text-slate-400"> +{r.expiredRoles} exp</span>}</td>
                <td className="px-3 py-2 text-slate-600 text-xs">{r.modules.join(' · ') || '-'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                  {r.idleDays == null ? '-' : r.idleDays}</td>
                <td className="px-3 py-2">
                  {r.findings.length === 0
                    ? <span className="text-xs text-emerald-600">Clean</span>
                    : r.findings.map(f => (
                        <span key={f} className={'inline-block mr-1 mb-1 px-2 py-0.5 rounded border text-xs ' + (FIND[f] ? FIND[f].tone : 'bg-slate-100 text-slate-600 border-slate-200')}>
                          {FIND[f] ? FIND[f].label : f}
                        </span>
                      ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
